import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Cpu, ArrowLeft, ArrowRightLeft, FileText, Target, Sparkles,
  AlertTriangle, Shield, Globe, ChevronDown, RefreshCw,
  Handshake, UserPlus, ArrowRight, BarChart3, Filter,
  BookmarkPlus, Bookmark, Thermometer, Package, Clock,
  CheckCircle, Activity, TrendingUp, TrendingDown, Minus,
  Box, Layers, Zap, Download, Lightbulb, Info, ShieldCheck,
  ExternalLink, Image, GitCompare,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, CartesianGrid, BarChart, Bar, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  ScatterChart, Scatter, ZAxis, Cell,
} from 'recharts';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import QuickAssignModal from '@/components/QuickAssignModal';
import api from '@/services/api';
import { useCurrency } from '@/context/CurrencyContext';
import { CPNBadge } from '@/hooks/useCPN';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';

/* ═══ Helper components ═══ */
const InfoRow = ({ label, value, tip }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-200 last:border-0">
    <span className="text-xs text-gray-500 flex items-center gap-1">{label} {tip && <InfoTip text={tip} />}</span>
    <span className="text-xs font-medium text-gray-900">{typeof value === 'string' || typeof value === 'number' ? value : value}</span>
  </div>
);

const riskColor = (r) => {
  if (!r) return 'bg-gray-100 text-gray-700';
  const l = r.toLowerCase?.() || '';
  if (l === 'high' || l === 'critical') return 'bg-red-100 text-red-800';
  if (l === 'medium') return 'bg-orange-100 text-orange-800';
  return 'bg-green-100 text-green-800';
};

const lcColor = (s) => {
  if (!s) return 'bg-gray-100 text-gray-800';
  const l = s.toLowerCase?.() || '';
  if (l === 'active') return 'bg-green-100 text-green-800';
  if (l === 'nrnd') return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

const TrendIcon = ({ trend }) => {
  if (trend === 'increasing') return <TrendingUp className="h-3.5 w-3.5 text-red-500" />;
  if (trend === 'decreasing') return <TrendingDown className="h-3.5 w-3.5 text-green-500" />;
  return <Minus className="h-3.5 w-3.5 text-gray-400" />;
};

const format = (v) => v != null ? `$${Number(v).toFixed(4)}` : '--';

/* ═══ Chart data generators ═══ */
const generatePriceHistory = (price) => {
  const months = ['Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb'];
  let p = price * 1.1;
  return months.map(m => { p = p * (1 + (Math.random() - 0.52) * 0.08); return { month: m, price: +p.toFixed(4), median: +(p * 0.92).toFixed(4) }; });
};
const generateForecast = (price) => ['Mar','Apr','May'].map((m, i) => ({ month: m, predicted: +(price * (1 + (i - 1) * 0.02)).toFixed(4), upper: +(price * (1 + i * 0.04)).toFixed(4), lower: +(price * (1 - i * 0.03)).toFixed(4) }));
const generateKDE = (median) => {
  const pts = []; for (let i = 0; i < 20; i++) { const x = median * (0.6 + i * 0.04); pts.push({ price: +x.toFixed(4), density: +(Math.exp(-0.5 * ((x - median) / (median * 0.15)) ** 2)).toFixed(3) }); } return pts;
};
const generatePriceVolume = (price) => Array.from({ length: 15 }, () => ({ qty: Math.floor(Math.random() * 10000 + 100), price: +(price * (0.7 + Math.random() * 0.6)).toFixed(4), fill: `hsl(${Math.floor(Math.random() * 120 + 100)}, 60%, 50%)` }));

/* ═══════════════════════════════════════════════════════════════
   COMPONENT DETAIL PAGE
   ═══════════════════════════════════════════════════════════════ */
const ComponentDetail = () => {
  const { mpn } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const originalMpn = searchParams.get('from');
  const { format: fmt } = useCurrency();
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('attributes');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const [saved, setSaved] = useState(false);
  const [compareChecked, setCompareChecked] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const toggleCompareCheck = useCallback((altMpn) => {
    setCompareChecked(prev => prev.includes(altMpn) ? prev.filter(m => m !== altMpn) : prev.length < 3 ? [...prev, altMpn] : prev);
  }, []);

  const runParametricCompare = useCallback(async () => {
    if (!compareChecked.length) return toast.error('Select at least 1 alternate to compare');
    setCompareLoading(true);
    try {
      const res = await api.post(`/components/${encodeURIComponent(mpn)}/parametric-compare`, { alternate_mpns: compareChecked });
      setCompareData(res.data);
      setCompareOpen(true);
    } catch { toast.error('Failed to load comparison data'); }
    finally { setCompareLoading(false); }
  }, [mpn, compareChecked]);

  const downloadCompareCSV = useCallback(() => {
    if (!compareData) return;
    const altHeaders = compareData.alternate_mpns.map(m => m);
    const header = ['Parameter', 'FFF', compareData.original_mpn, ...altHeaders].join(',');
    const rows = compareData.comparison.map(r => {
      const altVals = r.alternates.map(a => `"${a.value}"`);
      return [`"${r.parameter}"`, r.is_fff ? 'Yes' : 'No', `"${r.original}"`, ...altVals].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `parametric_compare_${mpn}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  }, [compareData, mpn]);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/components/detail/${encodeURIComponent(mpn)}`);
        setComp(res.data);
      } catch { toast.error('Failed to load component'); }
      finally { setLoading(false); }
    };
    if (mpn) fetchDetail();
  }, [mpn]);

  const priceHistory = useMemo(() => comp ? generatePriceHistory(comp.price) : [], [comp?.price]);
  const forecast = useMemo(() => comp ? generateForecast(comp.price) : [], [comp?.price]);
  const kdeData = useMemo(() => comp ? generateKDE(comp.median) : [], [comp?.median]);
  const pvData = useMemo(() => comp ? generatePriceVolume(comp.price) : [], [comp?.price]);

  const exportPDF = () => {
    if (!comp) return;
    const doc = createBrandedPDF(`${comp.mpn} — Component Report`, comp.description || '', {});
    let y = doc._startY;
    y = addSectionTitle(doc, 'Component Overview', y);
    y = addMetricCards(doc, [
      { label: 'Price', value: format(comp.price) },
      { label: 'Median', value: format(comp.median) },
      { label: 'Best Rate', value: format(comp.best_rate) },
      { label: 'Prob. Price', value: `$${(0.4 * comp.median + 0.6 * comp.best_rate).toFixed(4)}` },
      { label: 'Risk', value: comp.risk_level },
      { label: 'Lifecycle', value: comp.lifecycle },
    ], y);
    y = addSectionTitle(doc, 'Compliance', y);
    y = addTable(doc,
      ['Field', 'Status'],
      [['EU-RoHS', comp.eu_rohs ? 'Compliant' : 'Non-Compliant'], ['CN-RoHS', comp.china_rohs ? 'Compliant' : 'Non-Compliant'], ['REACH', comp.reach ? 'Compliant' : 'Non-Compliant'], ['PFAS', comp.fpas ? 'Non-Compliant' : 'Compliant'], ['Env. Compliance', comp.environmental_compliance || 'Low'], ['Qualifications', comp.qualifications || 'N/A'], ['Temp Range', comp.temp_range || 'N/A']],
      y
    );
    if (comp.distributor_prices?.length) {
      y = addSectionTitle(doc, 'Distributor Pricing', y);
      y = addTable(doc,
        ['Distributor', 'Country', 'Type', 'Rate', 'Price'],
        comp.distributor_prices.map(d => [d.name, d.country, d.type, d.rate_type, `$${d.price?.toFixed(4)}`]),
        y
      );
    }
    if (comp.alternates?.length) {
      y = addSectionTitle(doc, 'Alternates', y);
      y = addTable(doc,
        ['MPN', 'Manufacturer', 'Type', 'Match', 'Best Price', 'Median', 'Prob. Price'],
        comp.alternates.slice(0, 15).map(a => [a.mpn, a.manufacturer, a.type || a.match_type || 'FFF', `${a.match_pct || a.match_score || 0}%`, `$${a.price?.toFixed(4)}`, `$${a.median_price?.toFixed(4)}`, `$${(0.4 * (a.median_price || 0) + 0.6 * (a.price || 0)).toFixed(4)}`]),
        y
      );
    }
    downloadPDF(doc, `component-${comp.mpn}-report.pdf`);
    toast.success('PDF report downloaded');
  };

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div></Layout>
  );
  if (!comp) return (
    <Layout><div className="text-center py-20"><p className="text-gray-500">Component not found</p><Button variant="outline" onClick={() => navigate('/component-intelligence')} className="mt-4"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></div></Layout>
  );

  const anchor = (0.4 * comp.median + 0.6 * comp.best_rate).toFixed(4);

  return (
    <Layout title={comp.mpn} breadcrumbs={[
      { label: 'Part Intelligence', path: '/component-intelligence' },
      { label: comp.mpn || 'Component Detail' },
    ]}>
      <div className="space-y-4" data-testid="component-detail-page">
        {/* Alternate toggle bar */}
        {originalMpn && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2" data-testid="alternate-toggle-bar">
            <p className="text-sm text-blue-800">
              Viewing alternate of <button onClick={() => navigate(`/component-intelligence/${encodeURIComponent(originalMpn)}`)} className="font-semibold underline underline-offset-2 hover:text-blue-950 cursor-pointer">{originalMpn}</button>
            </p>
            <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(originalMpn)}`)} data-testid="back-to-original-btn">
              <ArrowLeft className="h-3 w-3 mr-1" /> Back to {originalMpn}
            </Button>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/component-intelligence')} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 font-mono">{comp.mpn}</h1>
                <CPNBadge mpn={comp.mpn} />
                <Badge className={riskColor(comp.risk_level)}>{comp.risk_level} Risk</Badge>
                <Badge className={lcColor(comp.lifecycle)}>{comp.lifecycle}</Badge>
                {comp.matched && <Badge className="bg-blue-100 text-blue-800 text-[10px]">Matched</Badge>}
                {comp.single_source && <Badge className="bg-red-100 text-red-800 text-[10px]">Single Source</Badge>}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{comp.manufacturer} | {comp.category} {comp.subcategory ? `> ${comp.subcategory}` : ''} | {comp.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSaved(!saved)} data-testid="save-btn">
              {saved ? <Bookmark className="h-4 w-4 text-yellow-500 fill-yellow-500" /> : <BookmarkPlus className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF} data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-1.5" /> PDF Report
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { l: 'Median (ALL)', v: format(comp.median), tip: 'Weighted median across all distributors', cl: 'text-blue-700' },
            { l: 'Best Price (ALL)', v: format(comp.best_rate), tip: 'Lowest verified distributor price globally', cl: 'text-green-700' },
            { l: 'Probabilistic Price', v: `$${anchor}`, tip: '0.4 × Weighted Median + 0.6 × Best Price. A probabilistic market-fair target.', cl: 'text-green-700' },
            { l: 'Lead Time', v: `${comp.lead_time_days || '--'}d`, tip: 'Typical supplier lead time in days' },
          ].map((c, i) => (
            <Card key={i} className="bg-white border">
              <CardContent className="p-2.5 text-center">
                <p className="text-[10px] text-gray-500 flex items-center justify-center gap-0.5">{c.l} <InfoTip text={c.tip} /></p>
                <p className={`font-bold text-sm ${c.cl || 'text-gray-900'}`}>{c.v}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabbed Analysis — 6 tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 max-w-5xl bg-gray-100">
            <TabsTrigger value="attributes" className="text-[11px] gap-1" data-testid="tab-attributes"><Cpu className="h-3 w-3" /> Attributes</TabsTrigger>
            <TabsTrigger value="distributors" className="text-[11px] gap-1" data-testid="tab-distributors"><Globe className="h-3 w-3" /> Distributors</TabsTrigger>
            <TabsTrigger value="risk" className="text-[11px] gap-1" data-testid="tab-risk"><Shield className="h-3 w-3" /> Risk</TabsTrigger>
            <TabsTrigger value="compliance" className="text-[11px] gap-1" data-testid="tab-compliance"><ShieldCheck className="h-3 w-3" /> Compliance</TabsTrigger>
            <TabsTrigger value="alternates" className="text-[11px] gap-1" data-testid="tab-alternates"><ArrowRightLeft className="h-3 w-3" /> Alternates</TabsTrigger>
            <TabsTrigger value="insights" className="text-[11px] gap-1" data-testid="tab-insights"><Lightbulb className="h-3 w-3" /> Insights</TabsTrigger>
          </TabsList>

          {/* ═══ Attributes Tab ═══ */}
          <TabsContent value="attributes" className="mt-4">
            {/* Top: Image + Component Identity */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Component Image */}
              <Card className="flex items-center justify-center bg-gray-50" data-testid="component-image-card">
                <CardContent className="p-4 w-full text-center">
                  {comp.image_url ? (
                    <img
                      src={comp.image_url}
                      alt={comp.mpn}
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      data-testid="component-image"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`${comp.image_url ? 'hidden' : 'flex'} flex-col items-center justify-center h-48 text-gray-400`}>
                    <Image className="h-12 w-12 mb-2" />
                    <p className="text-xs">Image available after enrichment</p>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2">Image is representative of component category</p>
                </CardContent>
              </Card>

              {/* Component Identity */}
              <Card className="md:col-span-2">
                <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-1.5"><Cpu className="h-4 w-4 text-green-600" /> Component Identity</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-0">
                  <InfoRow label="MPN" value={comp.mpn} tip="Manufacturer Part Number" />
                  <InfoRow label="CPN" value={<CPNBadge mpn={comp.mpn} />} tip="Customer Part Number (your internal reference)" />
                  <InfoRow label="Manufacturer" value={comp.manufacturer} tip="Component make/manufacturer" />
                  <InfoRow label="Series" value={comp.series || 'N/A'} tip="Product series or family" />
                  <InfoRow label="Matched" value={comp.matched ? <Badge className="bg-green-100 text-green-800 text-[10px]">Yes</Badge> : <Badge className="bg-gray-100 text-gray-800 text-[10px]">No</Badge>} tip="Whether this MPN was matched to a known component in the database" />
                  <InfoRow label="Brief Description" value={comp.description} tip="Short component description" />
                  <InfoRow label="Category" value={`${comp.category}${comp.subcategory ? ` > ${comp.subcategory}` : ''}`} />
                  <InfoRow label="Lifecycle" value={<Badge className={lcColor(comp.lifecycle)}>{comp.lifecycle}</Badge>} tip="Component lifecycle status (Active, NRND, EOL, Obsolete)" />
                  <InfoRow label="Datasheet" value={<a href={`https://www.alldatasheet.com/view.jsp?Searchword=${encodeURIComponent(comp.mpn)}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs flex items-center gap-1" data-testid="datasheet-link"><ExternalLink className="h-3 w-3" /> View Datasheet</a>} tip="Link to component datasheet" />
                </CardContent>
              </Card>
            </div>

            {/* Product Attributes — DigiKey-style comprehensive table */}
            {comp.params && Object.keys(comp.params).length > 0 && (
              <Card className="mt-4" data-testid="product-attributes-card">
                <CardHeader className="py-2 px-4 border-b bg-gray-50">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-blue-600" /> Product Attributes
                    <InfoTip text="Comprehensive technical specifications similar to distributor product pages" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableBody>
                      {Object.entries(comp.params).map(([key, val], i) => (
                        <TableRow key={key} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-testid={`attr-row-${i}`}>
                          <TableCell className="text-xs font-medium text-gray-600 w-1/3 py-2 border-r border-gray-200">{key}</TableCell>
                          <TableCell className="text-xs text-gray-900 py-2 font-medium">{val}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Packaging & Physical */}
            <Card className="mt-4" data-testid="packaging-card">
              <CardHeader className="py-2 px-4 border-b bg-gray-50">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Package className="h-4 w-4 text-amber-600" /> Packaging & Physical
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableBody>
                    {[
                      { k: 'Package / Case', v: comp.package },
                      { k: 'Supplier Device Package', v: comp.supplier_device_package || comp.package },
                      { k: 'Mounting Type', v: comp.mounting_type || 'Surface Mount' },
                      { k: 'UOM / Packaging', v: comp.uom_packaging || 'N/A' },
                    ].map((row, i) => (
                      <TableRow key={row.k} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <TableCell className="text-xs font-medium text-gray-600 w-1/3 py-2 border-r border-gray-200">{row.k}</TableCell>
                        <TableCell className="text-xs text-gray-900 py-2 font-medium">{row.v}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ Risk Tab ═══ */}
          <TabsContent value="risk" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Risk Summary */}
              <Card>
                <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-1.5"><Shield className="h-4 w-4 text-red-500" /> Risk Assessment</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-0">
                  <InfoRow label="Supply Chain Risk" value={<Badge className={riskColor(comp.risk_level)}>{comp.risk_level}</Badge>} tip="Overall supply chain risk based on availability, lead time, and geopolitical factors" />
                  <InfoRow label="Lifecycle Status" value={<Badge className={lcColor(comp.lifecycle)}>{comp.lifecycle}</Badge>} tip="Component lifecycle: Active, NRND (Not Recommended for New Design), EOL, Obsolete" />
                  <InfoRow label="Lifecycle Risk" value={<Badge className={riskColor(comp.lifecycle_risk)}>{comp.lifecycle_risk}</Badge>} tip="Risk level derived from lifecycle status" />
                  <InfoRow label="YTEOL" value={`${comp.yteol} months`} tip="Estimated months to End of Life" />
                  <InfoRow label="Single Source" value={comp.single_source ? <Badge className="bg-red-100 text-red-800 text-[10px]">Yes — High Risk</Badge> : <Badge className="bg-green-100 text-green-800 text-[10px]">No — {comp.alts_count} alternates</Badge>} tip="Whether this component has no known alternates (single source = higher risk)" />
                  <InfoRow label="Lead Time" value={`${comp.lead_time_days} days`} tip="Typical supplier lead time. >60 days = elevated risk" />
                  <InfoRow label="Risk Score" value={`${comp.risk_score}/10`} tip="Composite risk score (0-10)" />
                </CardContent>
              </Card>

              {/* Risk Radar */}
              <Card>
                <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Risk Radar</CardTitle></CardHeader>
                <CardContent className="p-2">
                  <ResponsiveContainer width="100%" height={220}>
                    <RadarChart data={[
                      { dim: 'Supply', val: comp.risk_level === 'High' ? 9 : comp.risk_level === 'Medium' ? 5 : 2 },
                      { dim: 'Lifecycle', val: comp.lifecycle === 'Active' ? 2 : comp.lifecycle === 'NRND' ? 6 : 9 },
                      { dim: 'Single Src', val: comp.single_source ? 9 : 2 },
                      { dim: 'Compliance', val: comp.environmental_compliance === 'High' ? 8 : comp.environmental_compliance === 'Medium' ? 5 : 2 },
                      { dim: 'Lead Time', val: comp.lead_time_days > 60 ? 8 : comp.lead_time_days > 30 ? 5 : 2 },
                    ]}>
                      <PolarGrid /><PolarAngleAxis dataKey="dim" tick={{ fontSize: 10 }} /><PolarRadiusAxis domain={[0, 10]} tick={false} />
                      <Radar dataKey="val" stroke="#ef4444" fill="#ef4444" fillOpacity={0.25} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Risk Breakdown */}
            <Card className="mt-4">
              <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Risk Breakdown</CardTitle></CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { type: 'Supply Chain', level: comp.risk_level, desc: 'Availability & lead time risk' },
                    { type: 'Lifecycle', level: comp.lifecycle_risk, desc: `${comp.lifecycle} — ${comp.yteol}mo YTEOL` },
                    { type: 'Single Source', level: comp.single_source ? 'High' : 'Low', desc: comp.single_source ? 'No alternates available' : `${comp.alts_count} alternates` },
                    { type: 'Compliance', level: comp.environmental_compliance || 'Low', desc: `EU-RoHS: ${comp.eu_rohs ? 'Yes' : 'No'}, CN-RoHS: ${comp.china_rohs ? 'Yes' : 'No'}` },
                    { type: 'Lead Time', level: comp.lead_time_days > 60 ? 'High' : comp.lead_time_days > 30 ? 'Medium' : 'Low', desc: `${comp.lead_time_days} days` },
                  ].map((r, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-gray-50 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">{r.type}</p>
                      <Badge className={`${riskColor(r.level)} mt-1`}>{r.level}</Badge>
                      <p className="text-[10px] text-gray-500 mt-1">{r.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ═══ Compliance Tab (NEW) ═══ */}
          <TabsContent value="compliance" className="mt-4" data-testid="compliance-tab-content">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-green-600" /> Environmental Compliance</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-0">
                  <InfoRow label="Environmental Compliance" value={<Badge className={riskColor(comp.environmental_compliance === 'Low' ? 'Low' : comp.environmental_compliance)}>{comp.environmental_compliance || 'Low'}</Badge>} tip="Overall environmental compliance risk level" />
                  <InfoRow label="EU-RoHS" value={comp.eu_rohs ? <Badge className="bg-green-100 text-green-800 text-[10px]">Compliant</Badge> : <Badge className="bg-red-100 text-red-800 text-[10px]">Non-Compliant</Badge>} tip="EU Restriction of Hazardous Substances (2011/65/EU)" />
                  <InfoRow label="CN-RoHS" value={comp.china_rohs ? <Badge className="bg-green-100 text-green-800 text-[10px]">Compliant</Badge> : <Badge className="bg-red-100 text-red-800 text-[10px]">Non-Compliant</Badge>} tip="China RoHS - Administrative Measures on the Control of Pollution by EEP" />
                  <InfoRow label="REACH" value={comp.reach ? <Badge className="bg-green-100 text-green-800 text-[10px]">Compliant</Badge> : <Badge className="bg-red-100 text-red-800 text-[10px]">Non-Compliant</Badge>} tip="Registration, Evaluation, Authorisation and Restriction of Chemicals" />
                  <InfoRow label="PFAS" value={comp.fpas ? <Badge className="bg-red-100 text-red-800 text-[10px]">Non-Compliant</Badge> : <Badge className="bg-green-100 text-green-800 text-[10px]">Compliant</Badge>} tip="Per- and Polyfluoroalkyl Substances — indicates if the component contains or is at risk for PFAS substances" />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm flex items-center gap-1.5"><Thermometer className="h-4 w-4 text-amber-500" /> Qualifications & Ratings</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-0">
                  <InfoRow label="Qualifications" value={comp.qualifications || 'N/A'} tip="Industry qualifications: AEC-Q100, AEC-Q200, MIL-STD-883, etc." />
                  <InfoRow label="Temperature Range" value={comp.temp_range || 'N/A'} tip="Operating temperature range" />
                  <InfoRow label="Temperature Grade" value={comp.temp_grade || 'Commercial'} tip="Commercial (-0 to 70C), Industrial (-40 to 85C), Military (-55 to 125C), Automotive (-40 to 150C)" />
                  <InfoRow label="Package" value={comp.package || 'N/A'} tip="Component package type" />
                  <InfoRow label="UOM / Packaging" value={comp.uom_packaging || 'N/A'} tip="Unit of Measure and packaging format" />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ═══ Alternates Tab ═══ */}
          <TabsContent value="alternates" className="mt-4">
            <Card>
              <CardHeader className="py-2 px-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <ArrowRightLeft className="h-4 w-4 text-purple-600" /> Alternates
                    <Badge className="bg-green-100 text-green-800 text-[10px] ml-1">{comp.alternates?.length || 0} found</Badge>
                    <InfoTip text="Form-Fit-Function (FFF) alternates with comparison pricing against your current component." />
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {comp.alternates?.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-3 px-1">
                      <p className="text-xs text-gray-500">{compareChecked.length > 0 ? `${compareChecked.length} selected (max 3)` : 'Select alternates to compare parametrically'}</p>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs" disabled={!compareChecked.length || compareLoading} onClick={runParametricCompare} data-testid="run-parametric-compare-btn">
                        {compareLoading ? <><RefreshCw className="h-3 w-3 mr-1 animate-spin" /> Loading...</> : <><GitCompare className="h-3 w-3 mr-1" /> Compare Selected ({compareChecked.length})</>}
                      </Button>
                    </div>
                    <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs w-8"></TableHead>
                        <TableHead className="text-xs">Alt MPN <InfoTip text="Alternate Manufacturer Part Number" /></TableHead>
                        <TableHead className="text-xs">Manufacturer <InfoTip text="Alternate manufacturer" /></TableHead>
                        <TableHead className="text-xs">Type <InfoTip text="Alternate type: FFF, Pin-to-Pin, Functional" /></TableHead>
                        <TableHead className="text-xs">Match <InfoTip text="Match score percentage" /></TableHead>
                        <TableHead className="text-xs">Best Price <InfoTip text="Lowest available price for this alternate" /></TableHead>
                        <TableHead className="text-xs">Median <InfoTip text="Alternate Median Price (ALL distributors)" /></TableHead>
                        <TableHead className="text-xs">Prob. Price <InfoTip text="Probabilistic Price = 0.4 × Median + 0.6 × Best Price" /></TableHead>
                        <TableHead className="text-xs">% Best Diff <InfoTip text="% difference in best price vs your component's best price" /></TableHead>
                        <TableHead className="text-xs">% Median Diff <InfoTip text="% difference in median price vs your component's median" /></TableHead>
                        <TableHead className="text-xs">% Prob. Diff <InfoTip text="% difference in probabilistic price vs your component's probabilistic price" /></TableHead>
                        <TableHead className="text-xs">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comp.alternates.map((alt, i) => (
                        <TableRow key={i} className="hover:bg-gray-50" data-testid={`alt-row-${i}`}>
                          <TableCell className="w-8">
                            <Checkbox checked={compareChecked.includes(alt.mpn)} onCheckedChange={() => toggleCompareCheck(alt.mpn)} data-testid={`alt-check-${i}`} />
                          </TableCell>
                          <TableCell className="font-mono text-xs font-medium">
                            <button onClick={() => navigate(`/component-intelligence/${encodeURIComponent(alt.mpn)}?from=${encodeURIComponent(comp.mpn)}`)} className="text-blue-700 hover:text-blue-900 underline underline-offset-2 cursor-pointer" data-testid={`alt-mpn-link-${i}`}>{alt.mpn}</button>
                          </TableCell>
                          <TableCell className="text-xs">{alt.manufacturer}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{alt.type || alt.match_type || 'FFF'}</Badge></TableCell>
                          <TableCell><Badge className="bg-green-100 text-green-800 text-[10px]">{alt.match_pct || alt.match_score || 0}%</Badge></TableCell>
                          <TableCell className="text-xs font-semibold text-green-700">${alt.best_price?.toFixed(4)}</TableCell>
                          <TableCell className="text-xs text-blue-700">${alt.median_price?.toFixed(4)}</TableCell>
                          <TableCell className="text-xs text-blue-700">${(0.4 * (alt.median_price || 0) + 0.6 * (alt.best_price || alt.price || 0)).toFixed(4)}</TableCell>
                          <TableCell><Badge className={`text-[10px] ${alt.pct_best_diff < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{alt.pct_best_diff > 0 ? '+' : ''}{alt.pct_best_diff}%</Badge></TableCell>
                          <TableCell><Badge className={`text-[10px] ${alt.pct_median_diff < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{alt.pct_median_diff > 0 ? '+' : ''}{alt.pct_median_diff}%</Badge></TableCell>
                          <TableCell><Badge className={`text-[10px] ${alt.pct_anchor_diff < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{alt.pct_anchor_diff > 0 ? '+' : ''}{alt.pct_anchor_diff}%</Badge></TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(alt.mpn)}?from=${encodeURIComponent(comp.mpn)}`)} data-testid={`alt-detail-btn-${i}`}>View</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>
                ) : (
                  <div className="text-center py-10 text-gray-500 text-sm">
                    <ArrowRightLeft className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    No alternates found for this component.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Parametric Comparison Dialog */}
            <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
              <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" aria-describedby="compare-desc">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <GitCompare className="h-5 w-5 text-blue-600" /> Parametric Comparison
                  </DialogTitle>
                  <p id="compare-desc" className="text-xs text-gray-500">
                    {compareData?.original_mpn} vs {compareData?.alternate_mpns?.join(', ')}
                  </p>
                </DialogHeader>
                {compareData && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                      <Badge className="bg-green-50 text-green-700 text-[9px]">Match</Badge>
                      <Badge className="bg-red-50 text-red-700 text-[9px]">FFF Mismatch</Badge>
                      <Badge className="bg-purple-100 text-purple-800 text-[9px]">FFF Parameter</Badge>
                      <div className="flex-1" />
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={downloadCompareCSV} data-testid="download-compare-csv">
                        <Download className="h-3 w-3 mr-1" /> Download CSV
                      </Button>
                    </div>
                    <div className="overflow-x-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50 text-xs">
                            <TableHead className="font-semibold text-xs w-48">Parameter</TableHead>
                            <TableHead className="font-semibold text-xs text-blue-700 bg-blue-50/40">{compareData.original_mpn} (Original)</TableHead>
                            {compareData.alternate_mpns.map((m, i) => <TableHead key={i} className="font-semibold text-xs">Alt: {m}</TableHead>)}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {compareData.comparison.map((row, idx) => (
                            <TableRow key={idx} className="text-xs" data-testid={`compare-row-${idx}`}>
                              <TableCell className="font-medium text-gray-700">
                                <span className="flex items-center gap-1">{row.parameter} {row.is_fff && <Badge className="text-[8px] bg-purple-100 text-purple-800 px-1">FFF</Badge>}</span>
                              </TableCell>
                              <TableCell className="bg-blue-50/20 font-medium">{row.original}</TableCell>
                              {row.alternates.map((a, i) => {
                                const fffMismatch = row.is_fff && !a.match && row.original !== '-' && a.value !== '-';
                                return (
                                  <TableCell key={i} className={fffMismatch ? 'bg-red-50' : a.match ? 'bg-green-50/50' : ''}>
                                    <span className={fffMismatch ? 'text-red-700 font-semibold' : a.match ? 'text-green-700' : ''}>
                                      {a.value}
                                    </span>
                                    {fffMismatch && <span className="text-[9px] text-red-500 ml-1">(mismatch)</span>}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ═══ Insights Tab (NEW) ═══ */}
          <TabsContent value="insights" className="mt-4" data-testid="insights-tab-content">
            <div className="space-y-4">
              {/* Buy Now / Hold Recommendation */}
              <Card className={`border-l-4 ${comp.buy_hold_recommendation === 'Buy' ? 'border-l-green-500 bg-green-50' : 'border-l-amber-500 bg-amber-50'}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${comp.buy_hold_recommendation === 'Buy' ? 'bg-green-200' : 'bg-amber-200'}`}>
                    {comp.buy_hold_recommendation === 'Buy' ? <TrendingDown className="h-6 w-6 text-green-700" /> : <Clock className="h-6 w-6 text-amber-700" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Recommendation: <span className={comp.buy_hold_recommendation === 'Buy' ? 'text-green-700' : 'text-amber-700'}>{comp.buy_hold_recommendation === 'Buy' ? 'Buy Now' : 'Hold'}</span></p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {comp.buy_hold_recommendation === 'Buy'
                        ? `Price trend is ${comp.price_trend}. Current price is ${comp.trend_pct > 0 ? 'above' : 'below'} recent average — favorable for purchasing.`
                        : `Price trend is ${comp.price_trend}. Market indicators suggest waiting for better pricing.`}
                    </p>
                  </div>
                  <InfoTip text="Buy/Hold recommendation based on price trends, supply/demand signals, and forecast models." />
                </CardContent>
              </Card>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Price Trends — 12-month History */}
                <Card data-testid="price-trends-chart">
                  <CardHeader className="py-2 px-4 border-b">
                    <CardTitle className="text-sm flex items-center gap-1.5"><Activity className="h-4 w-4 text-blue-600" /> Price Trends (12 months) <InfoTip text="Historical price vs median over the last 12 months" /></CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={priceHistory}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                        <Tooltip formatter={v => `$${Number(v).toFixed(4)}`} />
                        <Area type="monotone" dataKey="price" stroke="#2563eb" fill="#dbeafe" name="Price" />
                        <Area type="monotone" dataKey="median" stroke="#16a34a" fill="#dcfce7" name="Median" />
                        <ReferenceLine y={comp.best_rate} stroke="#22c55e" strokeDasharray="4 4" label={{ value: 'Best', fontSize: 9 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Price Forecast — 3 months */}
                <Card data-testid="price-forecast-chart">
                  <CardHeader className="py-2 px-4 border-b">
                    <CardTitle className="text-sm flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-purple-600" /> Price Forecast (3 months) <InfoTip text="AI-predicted price with upper and lower confidence bands" /></CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={forecast}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                        <Tooltip formatter={v => `$${Number(v).toFixed(4)}`} />
                        <Area type="monotone" dataKey="upper" stroke="transparent" fill="#fef3c7" name="Upper" />
                        <Area type="monotone" dataKey="predicted" stroke="#7c3aed" fill="#ede9fe" strokeWidth={2} name="Predicted" />
                        <Area type="monotone" dataKey="lower" stroke="transparent" fill="#fef3c7" name="Lower" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Price Distribution */}
                <Card data-testid="kde-chart">
                  <CardHeader className="py-2 px-4 border-b">
                    <CardTitle className="text-sm flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-emerald-600" /> Price Distribution <InfoTip text="Distribution of observed market prices. Peak = most common price point." /></CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={kdeData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="price" tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} /><YAxis tick={{ fontSize: 9 }} />
                        <Tooltip formatter={(v, name) => name === 'density' ? v.toFixed(3) : `$${v}`} />
                        <Area type="monotone" dataKey="density" stroke="#059669" fill="#d1fae5" name="density" />
                        <ReferenceLine x={comp.median} stroke="#2563eb" strokeDasharray="4 4" label={{ value: 'Median', fontSize: 9 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Price-Volume Relationship */}
                <Card data-testid="price-volume-chart">
                  <CardHeader className="py-2 px-4 border-b">
                    <CardTitle className="text-sm flex items-center gap-1.5"><Target className="h-4 w-4 text-orange-600" /> Price-Volume Relationship <InfoTip text="Scatter plot showing how price varies with order quantity. Larger dots = higher volumes." /></CardTitle>
                  </CardHeader>
                  <CardContent className="p-2">
                    <ResponsiveContainer width="100%" height={200}>
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="qty" name="Quantity" tick={{ fontSize: 9 }} /><YAxis dataKey="price" name="Price" tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} />
                        <ZAxis dataKey="qty" range={[20, 200]} /><Tooltip formatter={(v, name) => name === 'Price' ? `$${v}` : v.toLocaleString()} />
                        <Scatter data={pvData} fill="#f97316">
                          {pvData.map((_, i) => <Cell key={i} fill={pvData[i].fill} />)}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Market Signals */}
              <Card>
                <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Market Signals</CardTitle></CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { l: 'Supply', v: comp.supply_trend, icon: <TrendIcon trend={comp.supply_trend} /> },
                      { l: 'Demand', v: comp.demand_trend, icon: <TrendIcon trend={comp.demand_trend} /> },
                      { l: 'Price', v: comp.price_trend, icon: <TrendIcon trend={comp.price_trend} /> },
                    ].map((s, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                        {s.icon}
                        <div>
                          <p className="text-xs font-medium text-gray-900 capitalize">{s.v}</p>
                          <p className="text-[10px] text-gray-500">{s.l} Trend</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ═══ Distributors Tab ═══ */}
          <TabsContent value="distributors" className="mt-4">
            <Card>
              <CardHeader className="py-2 px-4 border-b">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Globe className="h-4 w-4 text-blue-600" /> All Distributors
                  <Badge variant="outline" className="text-[10px]">{comp.distributor_prices?.length || 0}</Badge>
                  <InfoTip text="Complete distributor listing with pricing, stock, lead time and rate type." />
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs">#</TableHead>
                      <TableHead className="text-xs">Distributor</TableHead>
                      <TableHead className="text-xs">Country</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Rate Type</TableHead>
                      <TableHead className="text-xs">Unit Price</TableHead>
                      <TableHead className="text-xs">Stock</TableHead>
                      <TableHead className="text-xs">Lead Time</TableHead>
                      <TableHead className="text-xs">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(comp.distributor_prices || []).sort((a, b) => a.price - b.price).map((d, i) => (
                        <TableRow key={i} data-testid={`full-dist-row-${i}`}>
                          <TableCell className="text-xs text-gray-400">{i + 1}</TableCell>
                          <TableCell className="text-xs font-medium">
                            {d.rate_type === 'Spot'
                              ? <span className="text-gray-900">{d.name}</span>
                              : <span className="text-gray-400 italic">Anonymous</span>
                            }
                          </TableCell>
                          <TableCell className="text-xs">{d.country}</TableCell>
                          <TableCell><Badge variant="outline" className={`text-[10px] ${d.type === 'Authorized' ? 'border-green-300 text-green-800' : 'border-orange-300 text-orange-800'}`}>{d.type}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className={`text-[10px] ${d.rate_type === 'Negotiated' ? 'border-blue-300 text-blue-800' : 'border-purple-300 text-purple-800'}`}>{d.rate_type}</Badge></TableCell>
                          <TableCell className="text-xs font-semibold">${d.price?.toFixed(4)}</TableCell>
                          <TableCell className="text-xs">{d.stock?.toLocaleString()}</TableCell>
                          <TableCell className="text-xs">{d.lead_time}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => toast.success(`Referred ${d.name} to 1Source`)} data-testid={`dist-refer-${i}`}>
                              <Handshake className="h-3 w-3 mr-1" /> Refer
                            </Button>
                          </TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-[10px] text-gray-400 p-2">Spot-priced distributors shown by name. Negotiated-rate distributors are anonymous.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Assign Modal */}
        <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
      </div>
    </Layout>
  );
};

export default ComponentDetail;
